import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
  Truck, MapPin, Phone, Mail, Calendar, CheckCircle, Send, 
  ArrowRight, ArrowLeft, Package, Home, Building2, User,
  Ruler, Clock, Shield, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const TOTAL_STEPS = 6;

const TYPES_LOGEMENT = [
  { value: 'studio', label: 'Studio', icon: '🏠', desc: '1 pièce principale' },
  { value: 'appartement', label: 'Appartement', icon: '🏢', desc: 'T1 à T5+' },
  { value: 'maison', label: 'Maison', icon: '🏡', desc: 'Pavillon, villa...' },
  { value: 'local', label: 'Local / Bureau', icon: '🏬', desc: 'Commerce, bureau' },
];

const NB_PIECES_OPTIONS = [
  { value: '1', label: '1 pièce', surface: '< 25 m²' },
  { value: '2', label: '2 pièces', surface: '25 - 40 m²' },
  { value: '3', label: '3 pièces', surface: '40 - 65 m²' },
  { value: '4', label: '4 pièces', surface: '65 - 85 m²' },
  { value: '5', label: '5 pièces', surface: '85 - 110 m²' },
  { value: '6+', label: '6 pièces et +', surface: '> 110 m²' },
];

const ETAGE_OPTIONS = [
  { value: '0', label: 'Rez-de-chaussée' },
  { value: '1', label: '1er étage' },
  { value: '2', label: '2e étage' },
  { value: '3', label: '3e étage' },
  { value: '4', label: '4e - 5e étage' },
  { value: '6', label: '6e étage et +' },
];

const OBJETS_LOURDS = [
  { id: 'piano', label: 'Piano' },
  { id: 'coffre_fort', label: 'Coffre-fort' },
  { id: 'machine_laver', label: 'Machine à laver' },
  { id: 'frigo_americain', label: 'Réfrigérateur américain' },
  { id: 'canape_angle', label: 'Canapé d\'angle' },
  { id: 'armoire_lourde', label: 'Armoire / Bibliothèque lourde' },
];

const OptionCard = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 ${
      selected
        ? 'border-orange-500 bg-orange-50 shadow-sm'
        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm active:scale-[0.98]'
    }`}
  >
    {children}
  </button>
);

const StepShell = ({ step, title, subtitle, icon: Icon, children, canContinue, onNext, onBack, isLast, loading }) => (
  <div className="min-h-[calc(100vh-140px)] flex flex-col">
    <div className="flex-1 flex flex-col justify-center max-w-lg mx-auto w-full px-4 py-6">
      {/* Step header */}
      <div className="mb-8 text-center">
        <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Icon className="w-7 h-7 text-orange-600" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1" data-testid={`step-title-${step}`}>{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>

      {/* Step content */}
      <div className="space-y-4 flex-1">
        {children}
      </div>

      {/* Navigation */}
      <div className="flex gap-3 mt-8 pt-4">
        {step > 1 && (
          <Button
            variant="ghost"
            onClick={onBack}
            className="min-h-[52px] px-6 text-slate-500 hover:text-slate-700"
            data-testid="btn-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        )}
        <Button
          onClick={isLast ? undefined : onNext}
          type={isLast ? 'submit' : 'button'}
          disabled={!canContinue || loading}
          className={`flex-1 min-h-[52px] text-base font-semibold rounded-2xl transition-all ${
            canContinue
              ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-md hover:shadow-lg'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
          data-testid={isLast ? 'btn-submit' : 'btn-next'}
        >
          {loading ? 'Envoi en cours...' : isLast ? 'Envoyer ma demande' : 'Continuer'}
          {!loading && (isLast ? <Send className="w-5 h-5 ml-2" /> : <ArrowRight className="w-5 h-5 ml-2" />)}
        </Button>
      </div>
    </div>
  </div>
);

const DemandeDevisDemenagement = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const topRef = useRef(null);

  const [form, setForm] = useState({
    // Step 1: Départ
    depart_adresse: '',
    depart_cp: '',
    depart_ville: '',
    // Step 2: Arrivée
    arrivee_adresse: '',
    arrivee_cp: '',
    arrivee_ville: '',
    // Step 3: Volume
    type_logement: '',
    nb_pieces: '',
    // Step 4: Date
    date_souhaitee: '',
    date_flexible: 'flexible',
    // Step 5: Conditions
    depart_etage: '',
    depart_ascenseur: '',
    arrivee_etage: '',
    arrivee_ascenseur: '',
    objets_lourds: [],
    besoin_emballage: '',
    besoin_demontage: '',
    // Step 6: Coordonnées
    nom: '',
    telephone: '',
    email: '',
    message: '',
  });

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleObjet = (id) => {
    setForm(prev => ({
      ...prev,
      objets_lourds: prev.objets_lourds.includes(id)
        ? prev.objets_lourds.filter(o => o !== id)
        : [...prev.objets_lourds, id]
    }));
  };

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [step]);

  const canNext = () => {
    switch (step) {
      case 1: return form.depart_adresse.trim() && form.depart_ville.trim();
      case 2: return form.arrivee_adresse.trim() && form.arrivee_ville.trim();
      case 3: return form.type_logement && form.nb_pieces;
      case 4: return form.date_souhaitee;
      case 5: return form.depart_etage !== '' && form.arrivee_etage !== '';
      case 6: return form.nom.trim() && form.telephone.trim();
      default: return true;
    }
  };

  const goNext = () => { if (canNext() && step < TOTAL_STEPS) setStep(step + 1); };
  const goBack = () => { if (step > 1) setStep(step - 1); };

  const handleSubmit = async () => {
    if (!canNext()) return;
    setLoading(true);
    try {
      const adresseDepart = `${form.depart_adresse}, ${form.depart_cp} ${form.depart_ville}`.trim();
      const adresseArrivee = `${form.arrivee_adresse}, ${form.arrivee_cp} ${form.arrivee_ville}`.trim();
      const typeLabel = TYPES_LOGEMENT.find(t => t.value === form.type_logement)?.label || form.type_logement;
      const piecesLabel = NB_PIECES_OPTIONS.find(p => p.value === form.nb_pieces)?.label || form.nb_pieces;
      const surfaceEstim = NB_PIECES_OPTIONS.find(p => p.value === form.nb_pieces)?.surface || '';

      const objetsLabels = form.objets_lourds.map(id => OBJETS_LOURDS.find(o => o.id === id)?.label || id);
      const etageD = ETAGE_OPTIONS.find(e => e.value === form.depart_etage)?.label || form.depart_etage;
      const etageA = ETAGE_OPTIONS.find(e => e.value === form.arrivee_etage)?.label || form.arrivee_etage;

      const messageComplet = [
        '--- DEPART ---',
        `Adresse: ${adresseDepart}`,
        `Etage: ${etageD} | Ascenseur: ${form.depart_ascenseur === 'oui' ? 'Oui' : form.depart_ascenseur === 'non' ? 'Non' : 'Non précisé'}`,
        '',
        '--- ARRIVEE ---',
        `Adresse: ${adresseArrivee}`,
        `Etage: ${etageA} | Ascenseur: ${form.arrivee_ascenseur === 'oui' ? 'Oui' : form.arrivee_ascenseur === 'non' ? 'Non' : 'Non précisé'}`,
        '',
        '--- LOGEMENT ---',
        `Type: ${typeLabel} | ${piecesLabel} (${surfaceEstim})`,
        '',
        '--- DETAILS ---',
        `Date: ${form.date_souhaitee} (${form.date_flexible === 'fixe' ? 'Date fixe' : form.date_flexible === 'flexible' ? 'Flexible +/- quelques jours' : 'Très flexible'})`,
        `Objets lourds: ${objetsLabels.length > 0 ? objetsLabels.join(', ') : 'Aucun'}`,
        `Emballage: ${form.besoin_emballage === 'oui' ? 'Oui, complet' : form.besoin_emballage === 'partiel' ? 'Partiel (fragiles)' : form.besoin_emballage === 'non' ? 'Non' : 'Non précisé'}`,
        `Démontage/Remontage: ${form.besoin_demontage === 'oui' ? 'Oui' : form.besoin_demontage === 'non' ? 'Non' : 'Non précisé'}`,
        form.message ? `\nMessage: ${form.message}` : '',
      ].join('\n');

      await axios.post(`${API}/api/demandes-client`, {
        request_type: 'demenagement',
        name: form.nom,
        phone: form.telephone,
        email: form.email,
        address_from: adresseDepart,
        address_to: adresseArrivee,
        volume: `${typeLabel} - ${piecesLabel} (${surfaceEstim})`,
        date: form.date_souhaitee,
        message: messageComplet
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center" data-testid="devis-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Demande envoyée !</h1>
          <p className="text-slate-600 mb-8 leading-relaxed">
            Merci <strong>{form.nom.split(' ')[0]}</strong> ! Notre équipe vous recontactera sous <strong>24h</strong> pour vous proposer un devis personnalisé.
          </p>
          <div className="bg-slate-50 p-5 rounded-2xl text-left mb-8 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Trajet</span>
              <span className="font-medium text-right">{form.depart_ville} → {form.arrivee_ville}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Date</span>
              <span className="font-medium">{new Date(form.date_souhaitee).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Logement</span>
              <span className="font-medium">{TYPES_LOGEMENT.find(t => t.value === form.type_logement)?.label} - {NB_PIECES_OPTIONS.find(p => p.value === form.nb_pieces)?.label}</span>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <a href="tel:0641888952" className="w-full">
              <Button variant="outline" className="w-full min-h-[48px] rounded-xl">
                <Phone className="w-4 h-4 mr-2" />
                Nous appeler
              </Button>
            </a>
            <a href="/" className="w-full">
              <Button className="w-full min-h-[48px] rounded-xl bg-orange-500 hover:bg-orange-600">
                Retour à l'accueil
              </Button>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" ref={topRef}>
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-100">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900 text-sm">DLV EXPRESS</span>
          </a>
          <span className="text-xs text-slate-400 font-medium">Étape {step}/{TOTAL_STEPS}</span>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-orange-500 transition-all duration-500 ease-out"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); if (step === TOTAL_STEPS) handleSubmit(); }}>

        {/* ÉTAPE 1 — Adresse de départ */}
        {step === 1 && (
          <StepShell
            step={1} title="D'où déménagez-vous ?" subtitle="Votre adresse actuelle"
            icon={MapPin} canContinue={canNext()} onNext={goNext} onBack={goBack}
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Adresse *</label>
              <Input
                data-testid="input-depart-adresse"
                value={form.depart_adresse}
                onChange={e => u('depart_adresse', e.target.value)}
                placeholder="14 rue de la Paix"
                className="h-12 rounded-xl text-base"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Code postal</label>
                <Input
                  data-testid="input-depart-cp"
                  value={form.depart_cp}
                  onChange={e => u('depart_cp', e.target.value)}
                  placeholder="75002"
                  className="h-12 rounded-xl text-base"
                  inputMode="numeric"
                />
              </div>
              <div className="col-span-3">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ville *</label>
                <Input
                  data-testid="input-depart-ville"
                  value={form.depart_ville}
                  onChange={e => u('depart_ville', e.target.value)}
                  placeholder="Paris"
                  className="h-12 rounded-xl text-base"
                />
              </div>
            </div>
          </StepShell>
        )}

        {/* ÉTAPE 2 — Adresse d'arrivée */}
        {step === 2 && (
          <StepShell
            step={2} title="Où allez-vous ?" subtitle="Votre nouvelle adresse"
            icon={MapPin} canContinue={canNext()} onNext={goNext} onBack={goBack}
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Adresse *</label>
              <Input
                data-testid="input-arrivee-adresse"
                value={form.arrivee_adresse}
                onChange={e => u('arrivee_adresse', e.target.value)}
                placeholder="25 avenue des Champs"
                className="h-12 rounded-xl text-base"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Code postal</label>
                <Input
                  data-testid="input-arrivee-cp"
                  value={form.arrivee_cp}
                  onChange={e => u('arrivee_cp', e.target.value)}
                  placeholder="92100"
                  className="h-12 rounded-xl text-base"
                  inputMode="numeric"
                />
              </div>
              <div className="col-span-3">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ville *</label>
                <Input
                  data-testid="input-arrivee-ville"
                  value={form.arrivee_ville}
                  onChange={e => u('arrivee_ville', e.target.value)}
                  placeholder="Boulogne"
                  className="h-12 rounded-xl text-base"
                />
              </div>
            </div>
          </StepShell>
        )}

        {/* ÉTAPE 3 — Type et taille du logement */}
        {step === 3 && (
          <StepShell
            step={3} title="Quel est votre logement ?" subtitle="Type et taille de votre logement actuel"
            icon={Home} canContinue={canNext()} onNext={goNext} onBack={goBack}
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-3 block">Type de logement *</label>
              <div className="grid grid-cols-2 gap-3">
                {TYPES_LOGEMENT.map(type => (
                  <OptionCard
                    key={type.value}
                    selected={form.type_logement === type.value}
                    onClick={() => u('type_logement', type.value)}
                    testId={`option-type-${type.value}`}
                  >
                    <div className="text-2xl mb-1">{type.icon}</div>
                    <div className="font-semibold text-sm text-slate-900">{type.label}</div>
                    <div className="text-xs text-slate-500">{type.desc}</div>
                  </OptionCard>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-3 block">Nombre de pièces *</label>
              <div className="grid grid-cols-2 gap-2">
                {NB_PIECES_OPTIONS.map(opt => (
                  <OptionCard
                    key={opt.value}
                    selected={form.nb_pieces === opt.value}
                    onClick={() => u('nb_pieces', opt.value)}
                    testId={`option-pieces-${opt.value}`}
                  >
                    <div className="font-semibold text-sm text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.surface}</div>
                  </OptionCard>
                ))}
              </div>
            </div>
          </StepShell>
        )}

        {/* ÉTAPE 4 — Date souhaitée */}
        {step === 4 && (
          <StepShell
            step={4} title="Quand souhaitez-vous déménager ?" subtitle="Choisissez votre date idéale"
            icon={Calendar} canContinue={canNext()} onNext={goNext} onBack={goBack}
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date souhaitée *</label>
              <Input
                data-testid="input-date"
                type="date"
                value={form.date_souhaitee}
                onChange={e => u('date_souhaitee', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="h-12 rounded-xl text-base"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-3 block">Flexibilité sur la date</label>
              <div className="space-y-2">
                {[
                  { value: 'fixe', label: 'Date fixe', desc: 'Je ne peux pas changer de date' },
                  { value: 'flexible', label: 'Flexible', desc: '+/- quelques jours possibles' },
                  { value: 'tres_flexible', label: 'Très flexible', desc: 'Je suis ouvert aux propositions' },
                ].map(opt => (
                  <OptionCard
                    key={opt.value}
                    selected={form.date_flexible === opt.value}
                    onClick={() => u('date_flexible', opt.value)}
                    testId={`option-flex-${opt.value}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-sm text-slate-900">{opt.label}</div>
                        <div className="text-xs text-slate-500">{opt.desc}</div>
                      </div>
                      {form.date_flexible === opt.value && (
                        <CheckCircle className="w-5 h-5 text-orange-500 shrink-0" />
                      )}
                    </div>
                  </OptionCard>
                ))}
              </div>
            </div>
          </StepShell>
        )}

        {/* ÉTAPE 5 — Conditions d'accès */}
        {step === 5 && (
          <StepShell
            step={5} title="Conditions d'accès" subtitle="Étage, ascenseur et objets spéciaux"
            icon={Building2} canContinue={canNext()} onNext={goNext} onBack={goBack}
          >
            {/* Départ */}
            <div>
              <p className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-2">Adresse de départ</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Étage *</label>
                  <select
                    data-testid="select-depart-etage"
                    value={form.depart_etage}
                    onChange={e => u('depart_etage', e.target.value)}
                    className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  >
                    <option value="">Choisir</option>
                    {ETAGE_OPTIONS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ascenseur</label>
                  <select
                    data-testid="select-depart-ascenseur"
                    value={form.depart_ascenseur}
                    onChange={e => u('depart_ascenseur', e.target.value)}
                    className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  >
                    <option value="">Choisir</option>
                    <option value="oui">Oui</option>
                    <option value="non">Non</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Arrivée */}
            <div>
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-2">Adresse d'arrivée</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Étage *</label>
                  <select
                    data-testid="select-arrivee-etage"
                    value={form.arrivee_etage}
                    onChange={e => u('arrivee_etage', e.target.value)}
                    className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  >
                    <option value="">Choisir</option>
                    {ETAGE_OPTIONS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ascenseur</label>
                  <select
                    data-testid="select-arrivee-ascenseur"
                    value={form.arrivee_ascenseur}
                    onChange={e => u('arrivee_ascenseur', e.target.value)}
                    className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  >
                    <option value="">Choisir</option>
                    <option value="oui">Oui</option>
                    <option value="non">Non</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Objets lourds */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Objets lourds ou encombrants
              </label>
              <div className="grid grid-cols-2 gap-2">
                {OBJETS_LOURDS.map(obj => (
                  <button
                    key={obj.id}
                    type="button"
                    onClick={() => toggleObjet(obj.id)}
                    data-testid={`toggle-objet-${obj.id}`}
                    className={`p-3 rounded-xl border-2 text-left text-sm transition-all ${
                      form.objets_lourds.includes(obj.id)
                        ? 'border-orange-500 bg-orange-50 font-medium'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {form.objets_lourds.includes(obj.id) ? '✓ ' : ''}{obj.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Emballage ?</label>
                <select
                  data-testid="select-emballage"
                  value={form.besoin_emballage}
                  onChange={e => u('besoin_emballage', e.target.value)}
                  className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                >
                  <option value="">Choisir</option>
                  <option value="non">Non</option>
                  <option value="partiel">Partiel (fragiles)</option>
                  <option value="oui">Oui, complet</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Démontage ?</label>
                <select
                  data-testid="select-demontage"
                  value={form.besoin_demontage}
                  onChange={e => u('besoin_demontage', e.target.value)}
                  className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                >
                  <option value="">Choisir</option>
                  <option value="non">Non</option>
                  <option value="oui">Oui</option>
                </select>
              </div>
            </div>
          </StepShell>
        )}

        {/* ÉTAPE 6 — Coordonnées */}
        {step === 6 && (
          <StepShell
            step={6} title="Vos coordonnées" subtitle="Pour que nous puissions vous recontacter"
            icon={User} canContinue={canNext()} onNext={handleSubmit} onBack={goBack} isLast loading={loading}
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom complet *</label>
              <Input
                data-testid="input-nom"
                value={form.nom}
                onChange={e => u('nom', e.target.value)}
                placeholder="Jean Dupont"
                className="h-12 rounded-xl text-base"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Téléphone *</label>
              <Input
                data-testid="input-telephone"
                value={form.telephone}
                onChange={e => u('telephone', e.target.value)}
                placeholder="06 XX XX XX XX"
                className="h-12 rounded-xl text-base"
                type="tel"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
              <Input
                data-testid="input-email"
                value={form.email}
                onChange={e => u('email', e.target.value)}
                placeholder="email@exemple.com (optionnel)"
                className="h-12 rounded-xl text-base"
                type="email"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Informations complémentaires</label>
              <textarea
                data-testid="input-message"
                value={form.message}
                onChange={e => u('message', e.target.value)}
                placeholder="Précisions utiles : accès difficile, parking, objets fragiles..."
                className="w-full h-24 rounded-xl border border-slate-200 px-4 py-3 text-base resize-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
              />
            </div>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-2xl text-sm">
              <p className="font-semibold text-orange-800 mb-1">Un conseiller vous rappelle sous 24h</p>
              <p className="text-orange-700 text-xs">
                Le devis final sera établi après échange détaillé sur vos besoins.
              </p>
            </div>
          </StepShell>
        )}

      </form>

      {/* Footer */}
      <div className="text-center py-4 text-xs text-slate-400 border-t">
        DLV Express - 06 41 88 89 52
      </div>
    </div>
  );
};

export default DemandeDevisDemenagement;
